/**
 * Custom controls overlay for `<PropertyMap>`. Replaces Google's default
 * UI (which is disabled via `disableDefaultUI: true`) with Intelligence
 * Terminal-styled buttons. Vertical strip in the top-right corner, mirroring
 * the original Google placement so muscle memory carries.
 */
import clsx from 'clsx';
import { Plus, Minus, Crosshair } from 'lucide-react';

export interface PropertyMapControlsProps {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onRecenter: () => void;
  className?: string;
}

export function PropertyMapControls({
  onZoomIn,
  onZoomOut,
  onRecenter,
  className,
}: PropertyMapControlsProps) {
  return (
    <div
      data-testid="property-map-controls"
      className={clsx(
        'absolute top-2 right-2 flex flex-col gap-1 z-10',
        className,
      )}
    >
      <ControlButton
        onClick={onZoomIn}
        ariaLabel="Zoom in"
        testid="property-map-zoom-in"
      >
        <Plus size={14} />
      </ControlButton>
      <ControlButton
        onClick={onZoomOut}
        ariaLabel="Zoom out"
        testid="property-map-zoom-out"
      >
        <Minus size={14} />
      </ControlButton>
      <ControlButton
        onClick={onRecenter}
        ariaLabel="Recenter on property"
        testid="property-map-recenter"
      >
        <Crosshair size={14} />
      </ControlButton>
    </div>
  );
}

interface ControlButtonProps {
  onClick: () => void;
  ariaLabel: string;
  testid: string;
  children: React.ReactNode;
}

function ControlButton({ onClick, ariaLabel, testid, children }: ControlButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      data-testid={testid}
      className={clsx(
        'flex items-center justify-center w-7 h-7 rounded-[2px]',
        'bg-panelBg/90 backdrop-blur border border-panelBorder',
        'text-text-muted hover:text-textBright hover:bg-panelBg',
        'focus:outline-none focus:ring-1 focus:ring-accent',
        'transition-colors',
      )}
    >
      {children}
    </button>
  );
}
